import React, { useState } from "react";
import { Send } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const STATUS_STYLES = {
  new: { bg: "#B5793F", label: "Received" },
  contacted: { bg: "#19B5DE", label: "In conversation" },
  closed: { bg: "#8A7E6B", label: "Closed" },
};

function RequestThread({ req, onReload }) {
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const st = STATUS_STYLES[req.status || "new"];

  const sendReply = async () => {
    if (!reply.trim()) return;
    setSending(true);
    const r = await fetch(`${API}/api/services/mine/${req.id}/reply`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ text: reply.trim() }),
    });
    setSending(false);
    if (r.ok) { setReply(""); toast.success("Sent — Rosh has been notified"); onReload(); }
    else toast.error("Could not send");
  };

  return (
    <article className="bg-white rounded-2xl p-5 border border-[#d4cbb8] shadow-sm" style={{ marginBottom: "20px" }} data-testid={`my-request-${req.id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-[10px] uppercase tracking-widest rounded-full px-2.5 py-1 text-[#F6F1E5]" style={{ background: st.bg }}>
          {st.label}
        </span>
        <span className="text-[11px] text-[#8A7E6B] ml-auto">
          {new Date(req.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
        </span>
      </div>

      <div className="mt-4" data-testid="my-services-list">
        {(req.messages || []).map((m, i) => (
          <div key={i} className={`flex ${m.from === "client" ? "justify-end" : "justify-start"}`}>
            <div className="max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap"
                 style={m.from === "client"
                   ? { background: "#262016", color: "#F6F1E5" }
                   : { background: "rgba(255,255,255,0.9)", color: "#262016", border: "1px solid rgba(38,32,22,0.1)" }}>
              {m.text}
              <span className="block mt-1.5 text-[10px] opacity-60">
                {m.from === "client" ? "You" : "Rosh"} · {new Date(m.ts).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
              </span>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 flex gap-2">
        <textarea rows={2} value={reply} onChange={(e) => setReply(e.target.value)}
                  placeholder="Write a reply — Rosh gets it instantly…"
                  data-testid={`my-request-reply-input-${req.id}`}
                  className="flex-1 rounded-xl border border-[#26201620] bg-white px-4 py-2.5 text-sm text-[#262016] outline-none focus:border-[#B5793F]" />
        <button onClick={sendReply} disabled={sending || !reply.trim()} data-testid={`my-request-reply-send-${req.id}`}
                className="self-end inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 text-sm text-[#F6F1E5] disabled:opacity-40"
                style={{ background: "#262016" }}>
          <Send size={13} /> {sending ? "…" : "Send"}
        </button>
      </div>
    </article>
  );
}

export default function MyServices({ requests, onReload }) {
  return (
    <div data-testid="my-services">
      <p className="rp-display text-2xl text-[#262016] mb-2">Your service requests</p>
      <p className="text-sm text-[#5C5042] mb-6">Replies land here and in your email inbox.</p>
      <div className="space-y-6">
        {requests.map((r) => <RequestThread key={r.id} req={r} onReload={onReload} />)}
      </div>
    </div>
  );
}
